import React, { useState, useEffect } from 'react';
import { 
  X, 
  Users, 
  Share2, 
  Copy, 
  Check,
  UserPlus,
  Link,
  Mail,
  Eye,
  EyeOff,
  Flame,
  Calendar,
  Percent
} from 'lucide-react';

const PARTNER_EMAIL_KEY = 'dmu-rimp-partner-email';
const SHARE_PROGRESS_KEY = 'dmu-rimp-share-progress';

const AccountabilityPartnerModal = ({ 
  isOpen, 
  onClose, 
  currentDay, 
  dailyNotes,
  retreatStartDate 
}) => {
  const [copied, setCopied] = useState(false);
  const [partnerEmail, setPartnerEmail] = useState('');
  const [savedPartnerEmail, setSavedPartnerEmail] = useState('');
  const [shareProgress, setShareProgress] = useState(true);
  const [showInviteSent, setShowInviteSent] = useState(false);
  
  const shareUrl = typeof window !== 'undefined' ? window.location.origin : '';
  
  // Calculate stats
  const completedDays = Object.keys(dailyNotes).filter(k => dailyNotes[k]?.trim()).length;
  const progressPercent = Math.round((completedDays / 30) * 100);
  
  // Calculate streak
  let streak = 0;
  for (let i = 1; i <= 30; i++) {
    if (dailyNotes[i]?.trim()) streak++;
    else break;
  }

  useEffect(() => {
    // Load saved settings
    const savedEmail = localStorage.getItem(PARTNER_EMAIL_KEY) || '';
    const savedShare = localStorage.getItem(SHARE_PROGRESS_KEY) !== 'false';
    setSavedPartnerEmail(savedEmail);
    setPartnerEmail(savedEmail);
    setShareProgress(savedShare);
  }, [isOpen]);

  const handleCopyLink = async () => {
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = async () => {
    const message = `Join me on the 30-Day Lenten Retreat (The RIMP Way)! 🙏\n\nI'm on Day ${currentDay || 1} with a ${streak}-day streak.\n\nStart your own journey: ${shareUrl}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join My Lenten Retreat',
          text: message,
          url: shareUrl
        });
      } catch (err) {
        handleCopyLink();
      }
    } else {
      handleCopyLink();
    }
  };

  const handleEmailInvite = () => {
    const subject = encodeURIComponent("🙏 Join me on a 30-Day Lenten Retreat!");
    const body = encodeURIComponent(
`Hi!

I'm doing a 30-Day Ignatian Lenten Retreat called "The RIMP Way" from Divine Mercy University, and I'd love for you to join me as an accountability partner!

I'm currently on Day ${currentDay || 1} with a ${streak}-day streak.

It's a "retreat in daily life" - you can do it anywhere, about 45 minutes a day:
• Morning: Scripture reading & reflection
• Midday: Quick heart-check
• Evening: The Examen prayer

The RIMP framework helps you discover:
❤️ Relationship - Who is God to you?
🪞 Identity - Who did God make you to be?
🎯 Mission - What are you called to do?
🧭 Purpose - Why are you here?

Start your journey here: ${shareUrl}

Let's do this together!
`
    );
    
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const handleSavePartner = () => {
    localStorage.setItem(PARTNER_EMAIL_KEY, partnerEmail);
    localStorage.setItem(SHARE_PROGRESS_KEY, shareProgress.toString());
    setSavedPartnerEmail(partnerEmail);
    setShowInviteSent(true);
    setTimeout(() => setShowInviteSent(false), 3000);
  };

  const handleRemovePartner = () => {
    localStorage.removeItem(PARTNER_EMAIL_KEY);
    setPartnerEmail('');
    setSavedPartnerEmail('');
  };

  const toggleShareProgress = () => {
    const newValue = !shareProgress;
    setShareProgress(newValue);
    localStorage.setItem(SHARE_PROGRESS_KEY, newValue.toString());
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10001] bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-hidden shadow-xl flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-[var(--primary)] to-[var(--primary-dark)] p-5 text-white flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Users className="w-6 h-6" />
              <h2 className="font-heading text-lg font-bold">Accountability Partner</h2>
            </div>
            <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-full transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-white/80 text-sm mt-2">
            Invite a friend to do the retreat with you!
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Your Progress Card */}
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-200">
            <p className="text-xs text-amber-700 font-bold uppercase tracking-wider mb-3">Your Progress</p>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div>
                <Calendar className="w-5 h-5 text-amber-600 mx-auto mb-1" />
                <p className="text-xl font-bold text-amber-800">{currentDay || 0}</p>
                <p className="text-xs text-amber-600">Day</p>
              </div>
              <div>
                <Flame className="w-5 h-5 text-orange-500 mx-auto mb-1" />
                <p className="text-xl font-bold text-orange-600">{streak}</p>
                <p className="text-xs text-orange-500">Streak</p>
              </div>
              <div>
                <Percent className="w-5 h-5 text-amber-600 mx-auto mb-1" />
                <p className="text-xl font-bold text-amber-800">{progressPercent}%</p>
                <p className="text-xs text-amber-600">Complete</p>
              </div>
            </div>
          </div>

          {/* Share Link */}
          <div className="space-y-3">
            <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Invite a Friend</p>
            <div className="flex gap-2">
              <div className="flex-1 bg-gray-100 rounded-lg px-3 py-2 text-sm text-gray-600 truncate">
                {shareUrl}
              </div>
              <button
                onClick={handleCopyLink}
                className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
              >
                {copied ? <Check className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5 text-gray-600" />}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={handleEmailInvite}
                className="py-3 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2"
              >
                <Mail className="w-5 h-5" />
                Email Invite
              </button>
              <button
                onClick={handleShare}
                className="py-3 bg-[var(--primary)] text-white rounded-xl font-medium hover:bg-[var(--primary-dark)] transition-colors flex items-center justify-center gap-2"
              >
                <Share2 className="w-5 h-5" />
                Share Link
              </button>
            </div>
          </div>

          {/* QR Code */}
          <div className="text-center">
            <p className="text-xs text-gray-500 font-bold uppercase tracking-wider mb-3">Or Scan QR Code</p>
            <img 
              src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(shareUrl)}&bgcolor=FFFFFF&color=5D1F2F`}
              alt="QR Code"
              className="w-32 h-32 mx-auto rounded-xl shadow-md"
            />
          </div>

          {/* Connect with Partner */}
          <div className="space-y-3 pt-4 border-t border-gray-100">
            <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Connect with a Partner</p>
            <p className="text-sm text-gray-600">
              Add your partner's email to stay connected. You'll be able to see each other's progress (not journal content).
            </p>
            
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Partner's Email</label>
              <input
                type="email"
                value={partnerEmail}
                onChange={(e) => setPartnerEmail(e.target.value)}
                placeholder="friend@example.com"
                className="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)]"
              />
            </div>

            {/* Share Progress Toggle */}
            <div 
              onClick={toggleShareProgress}
              className="flex items-center justify-between p-3 bg-gray-50 rounded-xl cursor-pointer"
            >
              <div className="flex items-center gap-2">
                {shareProgress ? (
                  <Eye className="w-5 h-5 text-green-600" />
                ) : (
                  <EyeOff className="w-5 h-5 text-gray-400" />
                )}
                <span className="text-sm text-gray-700">Share my progress with partner</span>
              </div>
              <div className={`w-12 h-6 rounded-full transition-colors ${shareProgress ? 'bg-green-500' : 'bg-gray-300'} flex items-center ${shareProgress ? 'justify-end' : 'justify-start'} px-1`}>
                <div className="w-4 h-4 rounded-full bg-white shadow" />
              </div>
            </div>

            <p className="text-xs text-gray-500">
              ℹ️ Your journal entries are always private. Only day progress, streak, and completion % are shared.
            </p>

            {savedPartnerEmail && (
              <div className="bg-green-50 rounded-xl p-3 border border-green-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <UserPlus className="w-5 h-5 text-green-600" />
                    <span className="text-sm text-green-800">Connected: {savedPartnerEmail}</span>
                  </div>
                  <button
                    onClick={handleRemovePartner}
                    className="text-xs text-red-500 hover:underline"
                  >
                    Remove
                  </button>
                </div>
              </div>
            )}

            {showInviteSent && (
              <div className="bg-green-100 rounded-xl p-3 text-center">
                <Check className="w-5 h-5 text-green-600 mx-auto mb-1" />
                <p className="text-sm text-green-700">Partner saved! Share the link so they can join.</p>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="p-4 bg-gray-50 border-t border-gray-100 flex gap-2 flex-shrink-0">
          <button
            onClick={onClose}
            className="flex-1 py-3 border border-gray-200 rounded-xl text-gray-600 font-medium hover:bg-gray-100 transition-colors"
          >
            Close
          </button>
          <button
            onClick={handleSavePartner}
            disabled={!partnerEmail || partnerEmail === savedPartnerEmail}
            className="flex-1 py-3 bg-[var(--primary)] text-white rounded-xl font-medium hover:bg-[var(--primary-dark)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <UserPlus className="w-4 h-4" />
            Save Partner
          </button>
        </div>
      </div>
    </div>
  );
};

export default AccountabilityPartnerModal;
